import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { makeBranchApi } from "../../api/branchApi";
import { useBranch } from "../../context/BranchContext";
import { StatCard, PageHeader, LoadingSpinner } from "../../components/ui";

export default function BranchDashboardPage() {
  const { branchId }      = useParams();
  const { branch }        = useBranch();
  const navigate          = useNavigate();
  const branchApi         = makeBranchApi(branchId);

  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    branchApi.dailyReport().catch(() => null)
      .then(r => { setStats(r?.data || null); })
      .finally(() => setLoading(false));
  }, [branchId]);

  const base = `/org/branches/${branchId}/manage`;

  const SECTIONS = [
    { icon: "🗂️", title: "Zones & Stations",  desc: "Configure zones, stations, counters and outcomes", path: `${base}/zones` },
    { icon: "👤", title: "Staff Management",   desc: "Add staff, assign stations, reset passwords",       path: `${base}/staff` },
    { icon: "⚙️", title: "Queue Settings",     desc: "Issuance mode, priority rules, notifications",      path: `${base}/settings` },
    { icon: "📺", title: "Display Board",      desc: "Configure what appears on patient-facing TVs",      path: `${base}/display` },
    { icon: "🔊", title: "Audio & Sounds",     desc: "Upload chime, digit clips, station name recordings", path: `${base}/audio` },
    { icon: "📊", title: "Reports",            desc: "Daily volume, staff performance, bottleneck analysis", path: `${base}/reports` },
  ];

  return (
    <div style={styles.root}>
      <PageHeader
        title={branch?.name || "Branch Dashboard"}
        subtitle={`Here's today's snapshot for this branch.`}
      />

      {loading ? <LoadingSpinner /> : stats ? (
        <div style={styles.statsRow}>
          <StatCard label="Tickets Today"  value={stats.total_tickets}  color="#60A5FA" icon="🎫" />
          <StatCard label="Completed"       value={stats.completed}      color="#22C55E" icon="✅" />
          <StatCard label="In Queue"        value={stats.still_in_queue} color="#EAB308" icon="⏳" />
          <StatCard label="Abandoned"       value={stats.abandoned}      color="#EF4444" icon="🚶" />
          <StatCard label="Completion Rate" value={stats.completion_rate != null ? `${stats.completion_rate}%` : null} color="#A78BFA" icon="📈" />
        </div>
      ) : (
        <div style={styles.noStats}>No activity yet today.</div>
      )}

      <h2 style={styles.sectionTitle}>Manage</h2>
      <div style={styles.grid}>
        {SECTIONS.map(s => (
          <button key={s.path} onClick={() => navigate(s.path)} style={styles.card}>
            <span style={styles.cardIcon}>{s.icon}</span>
            <div style={styles.cardBody}>
              <div style={styles.cardTitle}>{s.title}</div>
              <div style={styles.cardDesc}>{s.desc}</div>
            </div>
            <span style={styles.cardArrow}>›</span>
          </button>
        ))}
      </div>
    </div>
  );
}

const styles = {
  root:         { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "1100px" },
  statsRow:     { display: "flex", gap: "14px", flexWrap: "wrap", marginBottom: "32px" },
  noStats:      { color: "var(--text-muted)", fontSize: "14px", marginBottom: "32px" },
  sectionTitle: { fontSize: "13px", fontWeight: "700", color: "var(--text-muted)", letterSpacing: "0.08em", textTransform: "uppercase", margin: "0 0 14px" },
  grid:         { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px,1fr))", gap: "12px" },
  card: {
    display: "flex", alignItems: "center", gap: "14px",
    background: "var(--surface-bg)", border: "1px solid var(--surface-border)",
    borderRadius: "14px", padding: "18px 20px",
    cursor: "pointer", textAlign: "left", width: "100%",
    fontFamily: "inherit", transition: "border-color 0.15s, background 0.15s",
  },
  cardIcon:  { fontSize: "26px", flexShrink: 0 },
  cardBody:  { flex: 1, minWidth: 0 },
  cardTitle: { fontSize: "15px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "3px" },
  cardDesc:  { fontSize: "12px", color: "var(--text-muted)", lineHeight: "1.4" },
  cardArrow: { fontSize: "20px", color: "var(--text-faint)", flexShrink: 0 },
};
